import { api } from '@/api/client';
import { useQuery } from '@tanstack/react-query';

/**
 * Default-warehouse hook a POS-session-openhez (nagykör-4 FE-A).
 *
 * A `GET /warehouses/default` a JWT tenantId-jából oldja fel az alapértelmezett
 * raktárat. A `CashRegisterSession.locationId` FK valójában Warehouse-ra mutat
 * (a mezőnév „location", a séma Warehouse), így a session-openhez ennek az
 * endpointnak az `id`-ja kell — NEM a `User.locationId` (az másik tábla,
 * ráadásul a JWT-ben sincs benne).
 *
 * ⚠️ Empty-state gotcha: 0 default raktárnál a controller NEM dob 404-et, hanem
 * HTTP 200 + `{ error: { code: 'NOT_FOUND' } }` body-t ad. Ezért a body-ra
 * branch-elünk (hiányzó `data.id`), NEM a HTTP-statusra — különben az üres
 * eset némán „success"-ként esne át undefined warehouse-szal.
 */

interface DefaultWarehouseResponse {
  data?: { id: string; [key: string]: unknown };
  error?: { code: string; message: string };
}

export interface DefaultWarehouseResult {
  /** Az alapértelmezett raktár id-ja, vagy null ha nincs / még tölt. */
  warehouseId: string | null;
  isLoading: boolean;
  /** A fetch elszállt (hálózati hiba / nem-200) — megkülönböztetve az üres-állapottól. */
  isError: boolean;
  /** A tenantnak nincs alapértelmezett raktára (200 + error-body / hiányzó data). */
  isEmpty: boolean;
  /** Újrapróbálkozás a fetch-re (tranziens hálózati hiba után). */
  refetch: () => void;
}

export function useDefaultWarehouse(): DefaultWarehouseResult {
  const query = useQuery({
    queryKey: ['warehouses', 'default'],
    queryFn: async () => api.get<DefaultWarehouseResponse>('/warehouses/default'),
    staleTime: 5 * 60 * 1000,
  });

  const warehouseId = query.data?.data?.id ?? null;

  return {
    warehouseId,
    isLoading: query.isLoading,
    isError: query.isError,
    // A query sikeresen feloldott (200), de nincs használható raktár-id →
    // a tenantnak nincs alapértelmezett raktára (error-body vagy hiányzó data).
    isEmpty: query.isSuccess && warehouseId === null,
    refetch: () => {
      void query.refetch();
    },
  };
}
